import React, { useState, useEffect } from 'react';
import ImageUploader from '../components/ImageUploader';
import { buildApiUrl } from '../config/api';
import { parseMakeupApiResponse } from '../utils/makeupApi';

function setHtmlDarkMode(enabled) {
  if (enabled) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export default function HomePage() {
  // Dark Mode
  const [darkMode, setDarkMode] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);

  // Makeup Removal States
  const [processing, setProcessing] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [progressMsg, setProgressMsg] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    setHtmlDarkMode(darkMode);
  }, [darkMode]);

  // Makeup Removal Handler
  async function handleImageUpload(file) {
    setError(null);
    setResultUrl(null);
    setProgressMsg('Bild wird verarbeitet...');
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(buildApiUrl('/api/remove-makeup'), {
        method: 'POST',
        body: formData
      });
      const result = await parseMakeupApiResponse(res);

      if (result.type === 'image' || result.type === 'image-url') {
        setResultUrl(result.url);
        setProgressMsg('Fertig!');
        setProcessing(false);
      } else {
        setProgressMsg('Warte auf das Ergebnis...');
        pollResult(result.url);
      }
    } catch (err) {
      setError('Fehler beim Upload: ' + err.message);
      setProcessing(false);
      setProgressMsg('');
    }
  }

  async function pollResult(statusUrl) {
    try {
      let done = false;
      while (!done) {
        const res = await fetch(statusUrl);
        const data = await res.json();
        if (data.status === 'succeeded') {
          setResultUrl(data.output[0]);
          setProgressMsg('Fertig!');
          setProcessing(false);
          done = true;
        } else if (data.status === 'failed') {
          setError('Fehler beim Verarbeiten des Bildes');
          setProcessing(false);
          setProgressMsg('');
          done = true;
        } else {
          setProgressMsg('Verarbeitung läuft...');
          await new Promise(r => setTimeout(r, 2000));
        }
      }
    } catch (err) {
      setError('Fehler beim Abrufen des Ergebnisses: ' + err.message);
      setProcessing(false);
      setProgressMsg('');
    }
  }

  function handleReset() {
    setResultUrl(null);
    setError(null);
    setProcessing(false);
    setProgressMsg('');
  }

  return (
    <div className={darkMode ? 'dark' : ''}>
      <div className="min-h-screen bg-white dark:bg-background-dark transition-colors duration-500">
        {/* Toggle-Button für Dark/Light Mode */}
        <button
          onClick={() => setDarkMode((d) => !d)}
          className="fixed top-4 right-4 z-50 p-2 rounded-full bg-primary-600 dark:bg-primary-400 text-white dark:text-gray-900 shadow-lg focus:outline-none"
          title={darkMode ? 'Wechsel zu Light Mode' : 'Wechsel zu Dark Mode'}
          aria-label="Dark Mode Toggle"
        >
          {darkMode ? '🌙' : '☀️'}
        </button>

        <section className="py-12 px-4 max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4 text-primary-600 dark:text-primary-400">AI Makeup Remover</h2>
          <p className="mb-6 text-gray-600 dark:text-gray-300">Lade ein Portrait hoch und entferne Make-up mit KI!</p>
          <ImageUploader onImageUpload={handleImageUpload} disabled={processing} />
          {processing && (
            <div className="mt-4 text-sm text-gray-500 dark:text-gray-300">{progressMsg || 'Bitte habe einen Moment Geduld...'}</div>
          )}
          {(error || resultUrl) && (
            <button
              onClick={handleReset}
              className="mt-6 px-6 py-2 rounded bg-primary-500 hover:bg-primary-600 text-white font-medium shadow"
            >
              Zurücksetzen
            </button>
          )}
          {error && (
            <div className="mt-4 text-red-500 whitespace-pre-line break-words">
              {typeof error === 'string' ? error : JSON.stringify(error, null, 2)}
            </div>
          )}
          {resultUrl && (
            <div className="mt-8">
              <div className="font-semibold text-gray-700 dark:text-gray-200 mb-2">Ergebnis:</div>
              <img src={resultUrl} alt="Makeup entfernt" className="mx-auto rounded shadow max-w-full max-h-96" />
              <a href={resultUrl} download className="block mt-4 text-primary-600 dark:text-primary-300 underline">Bild herunterladen</a>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
